import BigNumber from 'bignumber.js'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { CurrencyAddressSearchLoupe } from '@root/entities/currency/ui/currency-address-search-loupe'
import { Socials } from '@root/entities/socials'
import { useTranslation } from '@root/shared/localization'
import { CopyButton } from '@root/shared/ui-kit'

import type { FavoriteUnavailable } from '@root/features/favorite-pairs/model/favorite-unavailable'

type FavoriteUnavailableCurrencyCellProps = {
  value: FavoriteUnavailable
}

export const FavoriteUnavailableCurrencyCell: React.FC<FavoriteUnavailableCurrencyCellProps> =
  React.memo(({ value }) => {
    const { t } = useTranslation()

    const currencyModel = useMemo(() => {
      return value.quoteTokenMeta
        ? new CurrencyModel({
            address: value.quoteTokenMeta.address,
            network: value.quoteTokenMeta.network,
            ticker: value.quoteTokenMeta.symbol,
            title: value.quoteTokenMeta.name,
            decimals: value.quoteTokenMeta.decimals,
            reusedImageCount: undefined,
            imageHash: undefined,
            socials: Socials.empty(),
            icon: undefined,
            iconStatus: undefined,
            moderationReason: undefined,
            supply: BigNumber(0),
            updatedAt: undefined,
          })
        : undefined
    }, [value.quoteTokenMeta])

    return (
      <Root>
        {value.quoteTokenMeta ? (
          <>
            <Icon>
              {currencyModel ? (
                <CurrencyIcon
                  currency={currencyModel}
                  withNetwork
                  withPreview
                  withPremiumFallback={false}
                  pairAddress={value.address}
                />
              ) : undefined}
            </Icon>
            <Content>
              <Header>
                {value.quoteTokenMeta.name}
                <CurrencyAddressSearchLoupe
                  size={14}
                  currencyAddress={value.quoteTokenMeta.address}
                  symbol={value.quoteTokenMeta.symbol}
                />
              </Header>

              <Body>
                <AddressLabel>{value.quoteTokenMeta.symbol}</AddressLabel>
                <CopyButton
                  size={12}
                  value={value.quoteTokenMeta.address}
                  tooltip={t('currency.token_address_copy_tooltip')}
                  snackbarText={t('currency.token_address_copied_message', {
                    ticker: value.quoteTokenMeta.symbol,
                  })}
                />
              </Body>
            </Content>
          </>
        ) : (
          '—'
        )}
      </Root>
    )
  })

const Root = styled.div`
  display: flex;

  gap: 6px;
  align-items: start;
  justify-content: start;
  padding-inline-end: 8px;
`

const Icon = styled.div``

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: start;
  justify-content: start;
`

const Header = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
`

const Body = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: var(--text-11);
  display: flex;
  gap: 2px;
`

const AddressLabel = styled(Typography)``
